import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Vehicle } from '../page';
import { useWishlist } from '../../../hooks/useWishlist';

interface VehicleGridProps {
  vehicles: Vehicle[];
  selectedForCompare: number[];
  setSelectedForCompare: (ids: number[]) => void;
}

export default function VehicleGrid({ vehicles, selectedForCompare, setSelectedForCompare }: VehicleGridProps) {
  const navigate = useNavigate();
  const [hoveredCard, setHoveredCard] = useState<number | null>(null);
  const { isInWishlist, toggleWishlist } = useWishlist();

  const handleViewDetails = (vehicle: Vehicle) => {
    navigate('/vehicle-details', { state: { vehicle } });
  };

  const toggleCompareSelection = (vehicleId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    if (selectedForCompare.includes(vehicleId)) {
      setSelectedForCompare(selectedForCompare.filter(id => id !== vehicleId));
    } else if (selectedForCompare.length < 4) {
      setSelectedForCompare([...selectedForCompare, vehicleId]);
    }
  };

  const handleWishlistToggle = (vehicle: Vehicle, e: React.MouseEvent) => {
    e.stopPropagation();
    toggleWishlist({
      id: vehicle.id,
      name: vehicle.name,
      price: vehicle.price,
      mileage: vehicle.mileage.toString(),
      location: vehicle.location,
      badge: vehicle.badge,
      images: vehicle.images
    });
  };

  if (vehicles.length === 0) {
    return (
      <div className="text-center py-20">
        <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <i className="ri-car-line text-5xl text-gray-400"></i>
        </div>
        <h3 className="text-2xl font-bold text-gray-900 mb-3">No vehicles found</h3>
        <p className="text-gray-600">Try adjusting your filters to see more results</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {vehicles.map((vehicle) => {
        const isSelected = selectedForCompare.includes(vehicle.id);
        const inWishlist = isInWishlist(vehicle.id);
        
        // Skip rendering the vehicle with id 39 (2017 Infiniti QX60) and id 38 (2016 Infiniti QX80)
        if (vehicle.id === 39 || vehicle.id === 38) {
          return null;
        }
        
        return (
          <div
            key={vehicle.id}
            className={`bg-white rounded-3xl overflow-hidden transition-all duration-300 cursor-pointer ${
              hoveredCard === vehicle.id ? 'transform -translate-y-2 shadow-2xl' : 'shadow-lg'
            } ${isSelected ? 'ring-4 ring-[#2563EB]' : ''}`}
            onMouseEnter={() => setHoveredCard(vehicle.id)}
            onMouseLeave={() => setHoveredCard(null)}
          >
            <div className="relative w-full h-64 bg-gray-100">
              {/* Vehicle Image */}
              {vehicle.images && vehicle.images.length > 0 && (
                <img
                  src={vehicle.images[0]}
                  alt={`${vehicle.year} ${vehicle.make} ${vehicle.model}`}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                  }}
                />
              )}

              {vehicle.badge && (
                <div className="absolute top-4 left-4 bg-[#2563EB] text-white px-4 py-1.5 rounded-full text-xs font-medium">
                  {vehicle.badge}
                </div>
              )}

              {/* Action Buttons */}
              <div className="absolute top-4 right-4 flex gap-2">
                {/* Compare Button */}
                <button
                  onClick={(e) => toggleCompareSelection(vehicle.id, e)}
                  className={`w-10 h-10 rounded-full flex items-center justify-center transition-all cursor-pointer shadow-lg ${
                    isSelected 
                      ? 'bg-[#2563EB] text-white' 
                      : 'bg-white/90 hover:bg-white text-gray-700'
                  }`}
                  title={isSelected ? 'Remove from comparison' : 'Add to comparison'}
                >
                  {isSelected ? (
                    <i className="ri-check-line text-xl"></i>
                  ) : (
                    <i className="ri-scales-line text-xl"></i>
                  )}
                </button>

                {/* Wishlist Button */}
                <button
                  onClick={(e) => handleWishlistToggle(vehicle, e)}
                  className={`w-10 h-10 rounded-full flex items-center justify-center transition-all cursor-pointer shadow-lg ${
                    inWishlist
                      ? 'bg-red-500 text-white hover:bg-red-600'
                      : 'bg-white/90 hover:bg-white text-gray-700'
                  }`}
                  title={inWishlist ? 'Remove from wishlist' : 'Add to wishlist'}
                >
                  <i className={`text-xl ${inWishlist ? 'ri-heart-fill' : 'ri-heart-line'}`}></i>
                </button>
              </div>
            </div>

            <div className="p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-2">{vehicle.name}</h3>
              <p className="text-sm text-gray-500 mb-4">
                {vehicle.mileage.toLocaleString()} miles • {vehicle.location}
              </p>
              <div className="flex items-center gap-2 mb-4 flex-wrap">
                <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-medium">
                  {vehicle.transmission}
                </span>
                <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-medium">
                  {vehicle.fuelType}
                </span>
                <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-medium">
                  {vehicle.bodyType}
                </span>
              </div>
              <p className="text-3xl font-bold text-gray-900 mb-6">${vehicle.price.toLocaleString()}</p>
              <button 
                onClick={() => handleViewDetails(vehicle)}
                className="w-full py-3.5 bg-[#2563EB] text-white rounded-full font-medium hover:bg-[#1d4ed8] transition-colors whitespace-nowrap"
              >
                View Details
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
